import {
  DefaultModality,
  TargetDisplaySize,
  VideoPreference,
  VideoPreferences,
  VideoPriorityBasedPolicy,
} from 'amazon-chime-sdk-js';
import { Layout } from "../video.types";
import type { TileState, VideoSourceState } from "../video.types";

type GridState = { layout: Layout };

type VideoSourceWithType = { attendeeId: string; type: VideoSourceType };

enum VideoSourceType {
  CONTENT_SHARE = 'contentShare',
  ACTIVE_SPEAKER = 'activeSpeaker',
  OTHER = 'other',
}

export const isContentShare = (sourceId: string): boolean =>
  new DefaultModality(sourceId).hasModality(DefaultModality.MODALITY_CONTENT);

/** Derive video-enabled from tile state: 'video' or 'frozen' means the
 *  downlink policy should subscribe to this attendee's stream. */
const isTileVideoEnabled = (state: TileState | undefined): boolean =>
  state === 'video' || state === 'frozen';

export const calculateVideoSourcesToBeRendered = (
  gridState: GridState,
  videoSourceState: VideoSourceState,
  attendeeTileState: Record<string, TileState>
): VideoSourceWithType[] => {
  const { layout } = gridState;
  const {
    activeSpeakersWithCameraSource,
    cameraSources,
    contentShareId,
    hasLocalVideo,
  } = videoSourceState;
  const videoSources: VideoSourceWithType[] = [];
  let commonSources: string[];

  // First, add content share
  for (const attendeeId of Object.keys(attendeeTileState)) {
    if (isContentShare(attendeeId) && isTileVideoEnabled(attendeeTileState[attendeeId])) {
      videoSources.push({ attendeeId, type: VideoSourceType.CONTENT_SHARE });
    }
  }

  // Second, add active speakers
  let activeSpeakers: string[] = [];
  let maximumNumberOfActiveSpeakers = 0;

  if (activeSpeakersWithCameraSource.length > 0) {

    if (layout === Layout.Gallery) {
      maximumNumberOfActiveSpeakers = 1;
    }
    if (layout === Layout.Featured) {
      maximumNumberOfActiveSpeakers =
        4 - (hasLocalVideo ? 1 : 0) - (contentShareId ? 1 : 0);
    }

    activeSpeakers = activeSpeakersWithCameraSource.slice(0, maximumNumberOfActiveSpeakers);

    videoSources.push(
      ...activeSpeakers.map(attendeeId => ({
        attendeeId,
        type: VideoSourceType.ACTIVE_SPEAKER,
      }))
    );

    commonSources = cameraSources.filter(
      id => !activeSpeakers.includes(id)
    );
  } else {
    commonSources = cameraSources;
  }


  // Last, add common video sources
  let gridSize = 0;

  if (layout === Layout.Gallery) {
    gridSize = Number.MAX_SAFE_INTEGER;
  }

  if (layout === Layout.Featured) {
    gridSize = 4;
  }

  const numberOfAvailableTiles = gridSize - (hasLocalVideo ? 1 : 0) - (contentShareId ? 1 : 0) - activeSpeakers.length;

  videoSources.push(
    ...commonSources
      .slice(0, numberOfAvailableTiles)
      .map(attendeeId => ({ attendeeId, type: VideoSourceType.OTHER }))
  );

  return videoSources;
};

export const updateDownlinkPreferences = (
  gridState: GridState,
  videoSourceState: VideoSourceState,
  attendeeTileState: Record<string, TileState>,
  priorityBasedPolicy: VideoPriorityBasedPolicy | undefined
): void => {

  if (!priorityBasedPolicy) {
    return;
  }
  const { layout } = gridState;
  const { hasLocalVideo } = videoSourceState;
  const videoPreferences = VideoPreferences.prepare();
  let targetDisplaySize: TargetDisplaySize;

  const videoSourcesToBeRendered = calculateVideoSourcesToBeRendered(
    gridState,
    videoSourceState,
    attendeeTileState
  );

  const numberOfTiles =
    videoSourcesToBeRendered.length + (hasLocalVideo ? 1 : 0);

  const HIGH_THRESHOLD = 4;
  const MEDIUM_THRESHOLD = 8;

  if (numberOfTiles <= HIGH_THRESHOLD) {
    targetDisplaySize = TargetDisplaySize.High;
  } else if (numberOfTiles <= MEDIUM_THRESHOLD) {
    targetDisplaySize = TargetDisplaySize.Medium;
  } else {
    targetDisplaySize = TargetDisplaySize.Low;
  }

  for (const videoSource of videoSourcesToBeRendered) {
    const { attendeeId, type } = videoSource;

    switch (type) {
      case VideoSourceType.CONTENT_SHARE: {
        const contentSize = numberOfTiles > MEDIUM_THRESHOLD
          ? TargetDisplaySize.Medium
          : TargetDisplaySize.High;
        videoPreferences.add(
          new VideoPreference(attendeeId, 1, contentSize)
        );
        break;
      }

      case VideoSourceType.ACTIVE_SPEAKER:
        videoPreferences.add(
          new VideoPreference(
            attendeeId,
            1,
            layout === Layout.Featured
              ? TargetDisplaySize.High
              : targetDisplaySize
          )
        );
        break;

      default:
        videoPreferences.add(
          new VideoPreference(attendeeId, 2, targetDisplaySize)
        );
        break;
    }
  }
  priorityBasedPolicy.chooseRemoteVideoSources(videoPreferences.build());
};
